import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { boats } from '../../../mocks/boats';
import { cars } from '../../../mocks/cars';
import { campers } from '../../../mocks/campers';
import { motorcycles } from '../../../mocks/motorcycles';
import { houses } from '../../../mocks/houses';

type CategoryKey = 'boats' | 'cars' | 'campers' | 'motorcycles' | 'houses';

const CATEGORY_IMAGES: Record<CategoryKey, string> = {
  boats: boats[0].image,
  cars: cars[0].image,
  campers: campers[0].image,
  motorcycles: motorcycles[0].image,
  houses: houses[0].image,
};

const CATEGORY_ROUTES: Record<CategoryKey, string> = {
  boats: '/bateaux',
  cars: '/voitures',
  campers: '/camping-cars',
  motorcycles: '/motos',
  houses: '/maison',
};

const topRow: CategoryKey[] = ['boats', 'cars', 'campers'];
const bottomRow: CategoryKey[] = ['motorcycles', 'houses'];

export default function OurFleet() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleSetTab = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        const route = CATEGORY_ROUTES[customEvent.detail as CategoryKey];
        if (route) navigate(route);
      }
    };
    window.addEventListener('setFleetTab', handleSetTab);
    return () => window.removeEventListener('setFleetTab', handleSetTab);
  }, [navigate]);

  const labels: Record<CategoryKey, string> = {
    boats: t('fleet.tabBoats'),
    cars: t('fleet.tabCars'),
    campers: t('fleet.tabCampers'),
    motorcycles: t('fleet.tabMotorcycles'),
    houses: t('fleet.tabHouses'),
  };

  const renderBubble = (key: CategoryKey) => (
    <button
      key={key}
      onClick={() => {
        navigate(CATEGORY_ROUTES[key]);
        window.scrollTo({ top: 0, behavior: 'instant' });
      }}
      className="flex flex-col items-center group cursor-pointer focus:outline-none"
    >
      <div className="relative transition-all duration-300 hover:scale-105 group-active:scale-95">
        {/* Ring */}
        <div className="absolute inset-0 rounded-full ring-2 ring-sand-200 ring-offset-2 group-hover:ring-ocean-400 group-hover:ring-offset-4 transition-all duration-300" />
        {/* Photo circle */}
        <div className="relative w-60 h-60 rounded-full overflow-hidden">
          <img
            src={CATEGORY_IMAGES[key]}
            alt={labels[key]}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 via-transparent to-transparent group-hover:from-coral-600/60 transition-all duration-300" />
          {/* Arrow on hover */}
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <div className="w-14 h-14 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-full">
              <i className="ri-arrow-right-line text-white text-2xl"></i>
            </div>
          </div>
        </div>
      </div>
      {/* Label — plus grand, sans prix */}
      <p className="mt-5 text-xl font-bold text-navy-800 group-hover:text-coral-500 transition-colors duration-200 tracking-wide">
        {labels[key]}
      </p>
    </button>
  );

  return (
    <section id="fleet" className="py-24 bg-gradient-to-b from-white via-sand-50/40 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-navy-800 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t('fleet.title')} <span className="text-ocean-500">{t('fleet.titleHighlight')}</span>
          </h2>
          <p className="text-lg text-navy-600 max-w-2xl mx-auto leading-relaxed">
            {t('fleet.description')}
          </p>
        </div>

        {/* Bubbles — 3 en haut, 2 centrées en bas */}
        <div className="flex flex-col items-center gap-12">
          {/* Ligne 1 */}
          <div className="flex flex-wrap items-start justify-center gap-10 md:gap-16">
            {topRow.map(renderBubble)}
          </div>
          {/* Ligne 2 */}
          <div className="flex flex-wrap items-start justify-center gap-10 md:gap-16">
            {bottomRow.map(renderBubble)}
          </div>
        </div>

      </div>
    </section>
  );
}
